import React, { useState } from 'react';
import {
  Box,
  Button,
  ClickAwayListener,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Popper,
  Typography,
} from '@mui/material';
import SpeedIcon from '@mui/icons-material/Speed';
import NotInterestedIcon from '@mui/icons-material/NotInterested';
import FastForwardIcon from '@mui/icons-material/FastForward';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import SyncAltIcon from '@mui/icons-material/SyncAlt';

const PACE_OPTIONS = [
  {
    icon: NotInterestedIcon,
    label: 'No pause',
    description: 'Move to the next sentence right away.',
    timingMode: 'fixedDelay',
    fixedDelaySeconds: 0,
  },
  {
    icon: FastForwardIcon,
    label: 'Short pause',
    description: 'A brief moment between sentences.',
    timingMode: 'fixedDelay',
    fixedDelaySeconds: 1,
  },
  {
    icon: PlayArrowIcon,
    label: 'Normal',
    description: 'A comfortable default pace.',
    timingMode: 'fixedDelay',
    fixedDelaySeconds: 2,
  },
  {
    icon: HourglassBottomIcon,
    label: 'Long pause',
    description: 'More time to repeat or think.',
    timingMode: 'fixedDelay',
    fixedDelaySeconds: 4,
  },
  {
    icon: SyncAltIcon,
    label: 'Match sentence length',
    description: 'Longer sentences get longer pauses.',
    timingMode: 'sentenceDuration',
    fixedDelaySeconds: null,
  },
];

function AutoplayTimingControls1({
  timingMode = 'fixedDelay',
  setTimingMode,
  fixedDelaySeconds = 1,
  setFixedDelaySeconds,
  triggerMode = 'button',
  triggerLabel = 'Autoplay pace',
  onTriggerClick,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const selectedOption =
    PACE_OPTIONS.find((option) => {
      if (option.timingMode !== timingMode) return false;
      if (option.timingMode === 'sentenceDuration') return true;
      return option.fixedDelaySeconds === Number(fixedDelaySeconds);
    }) || PACE_OPTIONS.find((option) => option.label === 'Short pause');

  const handleTriggerClick = (event) => {
    if (onTriggerClick) {
      onTriggerClick(event);
    }
    setAnchorEl(open ? null : event.currentTarget);
  };

  const handleSelect = (option) => {
    setTimingMode(option.timingMode);
    if (option.timingMode === 'fixedDelay') {
      setFixedDelaySeconds(option.fixedDelaySeconds);
    }
    setAnchorEl(null);
  };

  const trigger =
    triggerMode === 'menuItem' ? (
      <MenuItem onClick={handleTriggerClick}>
        <ListItemIcon>
          <SpeedIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText
          primary={triggerLabel}
          secondary={selectedOption.label}
          secondaryTypographyProps={{ fontSize: '0.78rem' }}
        />
      </MenuItem>
    ) : (
      <Button
        type="button"
        variant="outlined"
        size="small"
        startIcon={<SpeedIcon fontSize="small" />}
        onClick={handleTriggerClick}
        sx={{
          minWidth: 150,
          justifyContent: 'flex-start',
          textTransform: 'none',
          whiteSpace: 'nowrap',
        }}
      >
        Pace: {selectedOption.label}
      </Button>
    );

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
      {trigger}

      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        modifiers={[{ name: 'offset', options: { offset: [0, 4] } }]}
        sx={{ zIndex: (theme) => theme.zIndex.modal }}
      >
        <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
          <Paper
            elevation={3}
            sx={{
              width: 260,
              maxWidth: 'calc(100vw - 24px)',
              borderRadius: 1,
              overflow: 'hidden',
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                px: 1.5,
                pt: 1.25,
                pb: 0.5,
                color: 'text.secondary',
                fontWeight: 700,
              }}
            >
              Autoplay pace
            </Typography>
            <List dense disablePadding>
              {PACE_OPTIONS.map((option) => {
                const selected = option.label === selectedOption.label;
                const OptionIcon = option.icon;

                return (
                  <ListItemButton
                    key={option.label}
                    selected={selected}
                    onClick={() => handleSelect(option)}
                    sx={{ alignItems: 'flex-start', py: 1 }}
                  >
                    <ListItemIcon sx={{ minWidth: 36, pt: 0.35 }}>
                      <OptionIcon color={selected ? 'primary' : 'action'} fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={option.label}
                      secondary={option.description}
                      primaryTypographyProps={{ fontWeight: selected ? 700 : 500 }}
                      secondaryTypographyProps={{ fontSize: '0.78rem' }}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
}

export default AutoplayTimingControls1;
